import { useState } from 'react'
import type { GiveawayObjective } from '@/models/giveaway'
import { NewObjective } from '../giveaway-new/NewObjective'
import { ReferralIcon } from './ReferralIcon'

interface ObjectivesEditorProps {
  objectives: GiveawayObjective[]
  firstEntries: boolean
  onBack: () => void
  onSave: (objectives: GiveawayObjective[]) => void
}

/** A copy with a new value, leaving the original (and its prototype) intact. */
function withWorth(objective: GiveawayObjective, worth: number): GiveawayObjective {
  return Object.assign(Object.create(Object.getPrototypeOf(objective)), objective, {
    entries_worth: worth,
  })
}

/**
 * Edit a live giveaway's entry options: change what each is worth, remove
 * some, add new ones. Nothing is saved until the giveaway itself is.
 *
 * Removing an option only hides it — people who entered with it keep those
 * entries — so the screen says that rather than asking for confirmation.
 */
export function ObjectivesEditor({
  objectives: initial,
  firstEntries,
  onBack,
  onSave,
}: ObjectivesEditorProps) {
  const [objectives, setObjectives] = useState(initial)
  const [adding, setAdding] = useState(false)

  const hasReferral = objectives.some((objective) => objective.is_referral)
  // Referrals are credited when someone enters another way, so there has to
  // be another way.
  const hasDirectOption = objectives.some((objective) => !objective.is_referral)

  if (adding) {
    return (
      <NewObjective
        onCancel={() => setAdding(false)}
        onCreate={(objective) => {
          setObjectives((list) => [...list, objective])
          setAdding(false)
        }}
        // The form's own state can't survive leaving the page; there is no
        // draft to save for the edit screen.
        onBeforeLeave={() => {}}
        hasReferral={hasReferral}
        firstEntries={firstEntries}
      />
    )
  }

  return (
    <div className="content-section">
      <div className="field-row with-margin large-margin center" style={{ marginTop: 24 }}>
        <div className="field half button">
          <button type="button" className="field-box" onClick={() => setAdding(true)}>
            <div className="field-box-prepend">
              <img src="/icons/add_objective.png" alt="" />
            </div>
            <div className="field-box-content upper">Add New Option</div>
          </button>
        </div>
      </div>

      <div className="fields with-margin">
        {objectives.map((objective) => (
          <div className="field-row with-margin small-margin" key={objective.key}>
            <div className="field">
              <div className="field-box">
                <div className="field-box-prepend">
                  {objective.is_referral ? (
                    <ReferralIcon />
                  ) : (
                    <img src="/icons/discord.png" alt="" />
                  )}
                </div>
                <div className="field-box-content upper small objective-name">
                  <span>{objective.display_name}</span>
                  {objective.id === null ? <span className="field-box-hint">New</span> : null}
                </div>
                {/* A first-come referral earns a key, not entries. */}
                {objective.is_referral && firstEntries ? (
                  <div className="field-box-append small objective-worth-unit">Earns a key</div>
                ) : (
                  <>
                    <input
                      className="field-box-input objective-worth-input"
                      type="number"
                      min={1}
                      aria-label={`Entries for ${objective.display_name}${objective.is_referral ? ' per person referred' : ''}`}
                      value={objective.entries_worth ?? 1}
                      onChange={(event) => {
                        const worth = Math.max(1, Number(event.target.value) || 1)
                        setObjectives((list) =>
                          list.map((item) => (item === objective ? withWorth(item, worth) : item)),
                        )
                      }}
                    />
                    <div className="field-box-append small objective-worth-unit">
                      {objective.is_referral ? 'each' : 'entries'}
                    </div>
                  </>
                )}
                <button
                  type="button"
                  className="field-box-append"
                  aria-label={`Remove ${objective.display_name}`}
                  onClick={() => setObjectives((list) => list.filter((item) => item !== objective))}
                >
                  <img src="/icons/delete_objective.png" alt="" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {objectives.length === 0 ? (
          <div className="content-row small upper">
            No entry options — add at least one so people can enter.
          </div>
        ) : !hasDirectOption ? (
          <div className="content-row small upper">
            Add another entry option too — referred people need a way to enter.
          </div>
        ) : null}
      </div>

      <div className="content-row small with-margin">
        New values apply to everyone, including people who have already entered. Removing an option
        hides it, but people who already entered with it keep those entries.
      </div>

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="cta next"
            onClick={() => onSave(objectives)}
            disabled={!hasDirectOption}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

/** Whether the options differ from what the giveaway has now. */
export function objectivesChanged(
  before: GiveawayObjective[],
  after: GiveawayObjective[],
): boolean {
  return (
    before.length !== after.length ||
    after.some(
      (objective, i) =>
        objective.id === null ||
        objective.id !== before[i].id ||
        objective.entries_worth !== before[i].entries_worth,
    )
  )
}

/** The `giveaway_objective` list for PUT /giveaway/:id. */
export function objectivesPayload(objectives: GiveawayObjective[]): Record<string, unknown>[] {
  return objectives.map((objective) =>
    objective.id !== null
      ? { id: objective.id, entries_worth: objective.entries_worth ?? 1 }
      : {
          entries_worth: objective.entries_worth ?? 1,
          objective_id: objective.objective_id,
          objective_data_uuid: objective.objective_data_uuid,
          objective_data_name: objective.objective_data_name,
          ...(objective.name ? { name: objective.name } : {}),
        },
  )
}
